/**
 * OrgBootstrapGate.tsx — Cabinet-gate: blocks dashboard access until the user
 * belongs to at least one Organization. (WEB-282.1 M-spec, §2 cabinet-gate)
 *
 * Behaviour:
 * 1. On mount: fetches the user's org list via fetchOrganizationsThunk.
 * 2. While bootstrapping: renders nothing (prevents premature page load).
 * 3. After bootstrap, 0 orgs: shows a non-closable "Create your workspace" modal.
 * 4. After bootstrap, ≥1 org: renders children normally (gate passes).
 *
 * WEB-3796: the window is drawn after the rescin mockup (`ws-gate`,
 * `design/cabinet-rescin/app.jsx`, `WsForm` with `gate`): a plain box on the
 * empty page — no backdrop and nothing that closes it (no close button, no
 * Esc, no outside click, no Cancel), as `closable={false}` was before. Its
 * width is 560 (rule `.ws-gate` in `styles/cabinet-ws.css`) — the same as the
 * other "create a workspace" window (CreateOrganizationModal, WEB-2232):
 * windows of one kind share one width (WEB-2240). The failure screen follows
 * `ws-fail` of the same mockup. Both live outside the menu and the page, so
 * each has its own `.ds` root with theme attributes (`useCabinetRootAttributes`),
 * otherwise kit parts would have no variables. Create failure — kit toast.
 * On successful create, `orgs` in the Redux store gains an entry, `showGate`
 * becomes false, and the component switches to rendering children automatically.
 *
 * WEB-1314 — ПЯТЫЙ исход, которого раньше не было: ПЕРВАЯ загрузка списка НЕ
 * УДАЛАСЬ (обрыв связи, 500, таймаут). Раньше `.finally` объявлял загрузку
 * завершённой в обоих исходах, а единственным условием шлагбаума была длина
 * списка — поэтому сбой сети выглядел как «у вас нет ни одной организации», и
 * человек с настоящей организацией заводил вторую, лишнюю. Теперь исход
 * первой загрузки запоминается отдельно, и на отказе показывается экран «не
 * смогли узнать» с «Повторить» и выходом из аккаунта.
 *
 * ⚠ Почему НЕ читаем `error` из среза (как предлагала карточка). В это поле
 * пишут ОБА тханка: и загрузка списка, и СОЗДАНИЕ организации
 * (`organizationsSlice.ts`, `createOrganizationThunk.rejected`). Читать его
 * здесь значило бы при отказе создания подменить окно создания экраном
 * отказа и стереть введённое название. Кроме того, срез общий для всего
 * кабинета: провалившееся ФОНОВОЕ обновление списка (шапка зовёт тот же
 * тханк) выбросило бы работающего человека на экран ошибки. Локальное
 * состояние отвечает ровно на вопрос «чем кончилась первая загрузка» — оба
 * случая закрыты по построению (тесты (д)-(ж) в `*.web1314.test.tsx`).
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { localizeError } from '@/utils/localizeError';
import { Button, Icon, useToast } from '@/components/kit/ds';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import {
  fetchOrganizationsThunk,
  createOrganizationThunk,
} from '@/store/organizationsSlice';
import { logoutThunk } from '@/store/authSlice';
import {
  fetchProjectsThunk,
  setCurrentProject,
  switchActiveProjectThunk,
} from '@/store/projectsSlice';
import { PROJECT_LANDING } from '@/router/landingRoute';
import type { Project } from '@/types';
import { CreateProjectWizard } from '@/components/context/CreateProjectWizard';
import { useStrings } from '@/i18n';
import { settingsStrings } from '@/i18n/dictionaries/settings';
import '../../styles/cabinet-ws.css';

interface OrgBootstrapGateProps {
  children: React.ReactNode;
  /**
   * WEB-4117 (сверка вида 31.10, мелочь 1; эталон app.jsx:633–643 «кабинета за
   * окном нет»): шлагбаум закрыт — окно создания или экран отказа, то есть
   * первая загрузка кончилась, а организаций нет. Оболочка по нему прячет
   * боковое меню, капсулу и палитру ⌘K. Пока список грузится — открыт.
   */
  onBlockingChange?: (blocking: boolean) => void;
}

/** Предел длины названия — как у сервера (`CreateOrganizationDto`, 255). */
const NAME_MAX = 255;

export function OrgBootstrapGate({
  children,
  onBlockingChange,
}: OrgBootstrapGateProps) {
  const s = useStrings(settingsStrings);
  const dispatch = useAppDispatch();
  const { orgs, activeOrgId } = useAppSelector((state) => state.organizations);
  const navigate = useNavigate();
  // Шлагбаум рисуют и наборы без среза входа — читаем тему бережно.
  const userTheme = useAppSelector((state) => state.auth?.user?.theme);
  const root = useCabinetRootAttributes(userTheme);
  const [toast, showToast] = useToast();

  /** True once the initial org-fetch has completed (success or failure). */
  const [bootstrapped, setBootstrapped] = useState(false);
  /**
   * WEB-1314: отказ ПЕРВОЙ загрузки списка. null — либо ещё не пробовали,
   * либо последняя попытка удалась. Хранится разобранный отказ тханка
   * (`ApiErrorInfo`). WEB-3796: причину на экране больше не показываем
   * (эталон `ws-fail`, владелец 23.10: «много букв») — важен сам факт отказа.
   */
  const [bootstrapError, setBootstrapError] = useState<unknown>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // PM 2026-07-14: после создания орги сразу открываем визард создания проекта
  // (плавный onboarding). Визард закрываемый — если пропустить, остаётся баннер.
  const [showProjectWizard, setShowProjectWizard] = useState(false);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');

  /**
   * Одна дверь для первой загрузки и для кнопки «Повторить»: оба исхода
   * запоминаются ЯВНО. `.finally` здесь недостаточно — он не различает
   * «список пуст» и «не смогли узнать» (в этом и состоял дефект).
   */
  const loadOrganizations = useCallback(async () => {
    const result = await dispatch(fetchOrganizationsThunk());
    if (fetchOrganizationsThunk.rejected.match(result)) {
      setBootstrapError(result.payload ?? result.error);
    } else {
      setBootstrapError(null);
    }
    setBootstrapped(true);
  }, [dispatch]);

  useEffect(() => {
    // Fetch fresh org list on every dashboard entry.
    void loadOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await loadOrganizations();
    } finally {
      setIsRetrying(false);
    }
  };

  /**
   * Выход из аккаунта прямо с экрана отказа — чтобы человек не оказался
   * заперт, если повторы не помогают. Навигацию делать не нужно: после
   * `logoutThunk` состояние всего кабинета обнуляется (`store/index.ts`) и
   * `ProtectedRoute` сам уводит на /login — тем же путём, что кнопка «Выйти»
   * в шапке.
   */
  /**
   * WEB-3760 юнит 2б (решение владельца 23.10 `proj-wizard-2310`): авто-мастер
   * создал проект — сразу входим в него и ведём на «Главную» (там ход фонового
   * разбора), тем же набором, что переключатель проекта. Закрыт без проекта —
   * как было: остаёмся, плашка «нет проектов» на месте.
   */
  const handleWizardClose = (project?: Project) => {
    setShowProjectWizard(false);
    if (!project) return;
    dispatch(fetchProjectsThunk(activeOrgId ?? undefined));
    dispatch(setCurrentProject(project));
    void dispatch(switchActiveProjectThunk(project.id));
    navigate(PROJECT_LANDING);
  };

  const handleLogout = () => {
    void dispatch(logoutThunk());
  };

  const handleSubmit = async () => {
    // Проверка — как в эталоне (`WsForm`): пусто — «Укажите название…»,
    // длиннее предела — «не должно превышать 255», ошибка под полем.
    const value = name.trim();
    if (!value) {
      setNameError(s.orgGateNameRequired);
      return;
    }
    if (value.length > NAME_MAX) {
      setNameError(s.orgGateNameTooLong);
      return;
    }
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await dispatch(
        createOrganizationThunk({
          name: value,
        }),
      );

      if (createOrganizationThunk.rejected.match(result)) {
        // Ревизия пачки 29: после WEB-1955 отказ тханка — разобранный ApiErrorInfo,
        // не строка; показ идёт через localizeError (код → фраза по языку кабинета).
        showToast(localizeError(result.payload, s.orgCreateFailed), 'err');
      } else {
        // On fulfilled: Redux store gains an org (showGate → false) и сразу
        // открываем визард создания первого проекта.
        setShowProjectWizard(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // WEB-4117: до отрисовки кадра (useLayoutEffect) — иначе на миг видно меню
  // вокруг окна. Условие — то же, что у обоих экранов ниже (orgs.length === 0).
  const blocking = bootstrapped && orgs.length === 0;
  useLayoutEffect(() => {
    onBlockingChange?.(blocking);
  }, [blocking, onBlockingChange]);

  // While the initial org-fetch is in-flight, render nothing. This prevents
  // dashboard pages from mounting (and making unnecessary API calls) before
  // the gate check has completed.
  if (!bootstrapped) {
    return null;
  }

  /**
   * WEB-1314, решение владельца 13.09 (вариант А): при отказе первой загрузки
   * показываем экран отказа, а НЕ окно создания — оно и соблазняло человека
   * завести лишнюю организацию. Условие `orgs.length === 0` здесь второе и
   * намеренное: если список всё же есть (успех был раньше), никакой отказ
   * кабинет не закрывает. WEB-3796: вид — `ws-fail` эталона.
   */
  if (bootstrapError !== null && orgs.length === 0) {
    return (
      <div {...root}>
        <div className="ws-fail" data-testid="org-bootstrap-error">
          <Icon name="error" size={40} />
          <h2>{s.orgBootstrapFailedTitle}</h2>
          <p>{s.orgBootstrapFailedHint}</p>
          <div className="row">
            <Button
              kind="primary"
              loading={isRetrying}
              onClick={() => void handleRetry()}
              data-testid="org-bootstrap-retry-btn"
            >
              {s.orgBootstrapRetry}
            </Button>
            <Button
              kind="ghost"
              onClick={handleLogout}
              data-testid="org-bootstrap-logout-btn"
            >
              {s.orgBootstrapLogout}
            </Button>
          </div>
          <p className="s">{s.orgBootstrapFailedSupport}</p>
        </div>
      </div>
    );
  }

  const showGate = orgs.length === 0;

  if (showGate) {
    return (
      <div {...root}>
        {toast}
        <div
          className="ws-gate"
          role="dialog"
          aria-label={s.orgGateTitle}
          data-testid="org-gate-modal"
        >
          <div className="mh">{s.orgGateTitle}</div>
          <div className="mb">
            <label className="lbl">
              <span>{s.orgCreateNameLabel}</span>
              <input
                className={'input' + (nameError ? ' bad' : '')}
                autoComplete="off"
                data-testid="org-gate-name-input"
                placeholder={s.orgGateNamePlaceholder}
                maxLength={NAME_MAX}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleSubmit();
                }}
                autoFocus
              />
              {nameError && (
                <span className="hint bad" data-testid="org-gate-name-error">
                  {nameError}
                </span>
              )}
            </label>
          </div>
          <div className="mf">
            <Button
              kind="primary"
              loading={isSubmitting}
              onClick={() => void handleSubmit()}
              data-testid="org-gate-submit-btn"
            >
              {s.orgGateSubmit}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      {/* Авто-визард создания проекта сразу после создания орги (PM 2026-07-14). */}
      <CreateProjectWizard
        open={showProjectWizard}
        onClose={handleWizardClose}
      />
    </>
  );
}
